import { apiBlog } from "@/api/blog.api.ts";
import { apiComment } from "@/api/comment.api.ts";
import { apiNotifications } from "@/api/notification.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeeTodayKeys } from "@/queries/employees/use-employee-today.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiBlogChannelCan,
  ApiBlogChannelFilter,
  ApiBlogChannelSave,
  ApiBlogCommentsSearchFilter,
  ApiBlogPost,
  ApiBlogPostSave,
  ApiBlogPostShort,
  ApiBlogPostsSearchFilter,
  ApiBlogPostViewersFilter,
} from "@/types/blog.types.ts";
import { ApiComment } from "@/types/comment.types.ts";
import { ApiPagedData } from "@skailer-com/uikit/core";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import { Control, FieldValues } from "react-hook-form";

export const blogKeys = createQueryKeys("blog", {
  searchPostsInfinite: (filter: ApiBlogPostsSearchFilter) => ({
    queryKey: [filter],
  }),
  searchCommentsInfinite: (filter: ApiBlogCommentsSearchFilter) => ({
    queryKey: [filter],
  }),
  myChannels: (filter: ApiBlogChannelFilter) => ({
    queryKey: [filter],
  }),
  postViewers: (filter: ApiBlogPostViewersFilter) => ({
    queryKey: [filter],
  }),

  postDetails: (id: number) => ({
    queryKey: [id],
    queryFn: async () => {
      const result = await apiBlog.getPostById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  myChannelsUnreadCount: () => ({
    queryKey: ["unread_post_count"],
    queryFn: async () => {
      const result = await apiBlog.getMyChannelsUnreadCount();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  channelDetails: (id: number) => ({
    queryKey: [id],
    queryFn: async () => {
      const result = await apiBlog.getMyChannelById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useMyChannelDetails = (id: number) => {
  return useQuery({
    ...blogKeys.channelDetails(id),
  });
};

export const useBlogMyChannels = (
  filter: ApiBlogChannelFilter | undefined = { can: ApiBlogChannelCan.view }
) => {
  return useQuery({
    ...blogKeys.myChannels(filter),
    queryFn: async () => {
      const result = await apiBlog.getMyChannels(filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useBlogUpdateMyChannels = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (channel_ids: number[]) {
      const result = await apiBlog.updateMyChannels(channel_ids);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return { updatedData: result.data, channel_ids };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: blogKeys.myChannels._def,
      });

      queryClient.invalidateQueries({
        queryKey: blogKeys.channelDetails._def,
        exact: false,
      });
    },
  });
};

export const useBlogAddReaction = () => {
  const queryClient = useQueryClient();
  const { data: employeeMe } = useEmployeeMe();

  return useMutation({
    mutationFn: async function ({
      id,
      emoji,
      isNew,
    }: {
      id: number;
      emoji: string;
      isNew: boolean;
    }) {
      const result = isNew
        ? await apiBlog.addReaction(id, emoji)
        : await apiBlog.deleteReaction(id, emoji);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return { result: result.data, postId: id, isNew, emoji };
    },
    onSuccess: ({ postId, isNew, emoji }) => {
      const reactionsAfterUpdate = (post: ApiBlogPostShort) => {
        const existingReactionIndex = post.reactions?.findIndex((r) => r.emoji === emoji) ?? -1;

        let updatedReactions = [...(post.reactions || [])];

        if (isNew) {
          if (existingReactionIndex !== -1) {
            updatedReactions[existingReactionIndex] = {
              ...updatedReactions[existingReactionIndex],
              count: updatedReactions[existingReactionIndex].count + 1,
              is_mine: true,
              employee_ids: employeeMe
                ? [...updatedReactions[existingReactionIndex].employee_ids, employeeMe.id]
                : [...updatedReactions[existingReactionIndex].employee_ids],
            };
          } else {
            updatedReactions.push({
              emoji,
              count: 1,
              is_mine: true,
              employee_ids: employeeMe ? [employeeMe.id] : [],
            });
          }
        } else {
          if (existingReactionIndex !== -1) {
            const currentReaction = updatedReactions[existingReactionIndex];

            if (currentReaction.count <= 1) {
              updatedReactions = updatedReactions.filter((r) => r.emoji !== emoji);
            } else {
              updatedReactions[existingReactionIndex] = {
                ...currentReaction,
                count: currentReaction.count - 1,
                is_mine: false,
                employee_ids: currentReaction.employee_ids.filter((id) => id !== employeeMe?.id),
              };
            }
          }
        }

        return updatedReactions;
      };

      queryClient.setQueryData(
        blogKeys.postDetails(postId).queryKey,
        (post: ApiBlogPost | undefined) => {
          if (!post) return post;

          return {
            ...post,
            reactions: reactionsAfterUpdate(post),
          };
        }
      );

      queryClient.setQueriesData<InfiniteData<ApiPagedData<ApiBlogPostShort>>>(
        { queryKey: blogKeys.searchPostsInfinite._def, exact: false },
        (data) => {
          if (!data) return data;

          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              items: page.items.map((post) =>
                post.id === postId
                  ? {
                      ...post,
                      reactions: reactionsAfterUpdate(post),
                    }
                  : post
              ),
            })),
          };
        }
      );
    },
  });
};

export const useBlogReadPost = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({ id }: { id: number }) {
      return { postId: id };
    },
    onSuccess: ({ postId }) => {
      queryClient.setQueryData(
        blogKeys.postDetails(postId).queryKey,
        (post: ApiBlogPost | undefined) => {
          if (!post) return post;

          return {
            ...post,
            is_read: true,
          };
        }
      );

      queryClient.setQueriesData<InfiniteData<ApiPagedData<ApiBlogPostShort>>>(
        { queryKey: blogKeys.searchPostsInfinite._def, exact: false },
        (data) => {
          if (!data) return data;

          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              items: page.items.map((post) =>
                post.id === postId
                  ? {
                      ...post,
                      is_read: true,
                    }
                  : post
              ),
            })),
          };
        }
      );

      queryClient.invalidateQueries({
        queryKey: blogKeys.myChannelsUnreadCount._def,
      });

      queryClient.invalidateQueries({
        queryKey: employeeTodayKeys.getToday._def,
      });
    },
  });
};

export const useBlogAddComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      text,
      postId,
      fileUuids,
    }: {
      text: string;
      postId: number;
      fileUuids?: string[];
    }) {
      const result = await apiBlog.addComment(postId, { text, file_uuids: fileUuids });
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return { postId, comment: result.data };
    },
    onSuccess: ({ postId }) => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          if (query.queryKey[0] !== "blog") return false;

          const queryFnType = query.queryKey[1];
          if (queryFnType !== "searchCommentsInfinite") return false;

          try {
            const filter = query.queryKey[2] as ApiBlogCommentsSearchFilter;
            return filter.post_id === postId;
          } catch (err) {
            console.log(err);
            return false;
          }
        },
      });
    },
  });
};

let commentIdsBuffer: number[] = [];
let timerForReadComments: NodeJS.Timeout | null = null;

export const useBlogReadComments = () => {
  const queryClient = useQueryClient();

  const updateReadFlagInCache = (postId: number, commentId: number) => {
    queryClient.setQueriesData<InfiniteData<ApiPagedData<ApiComment>>>(
      {
        predicate: (query) => {
          if (query.queryKey[0] !== "blog") return false;
          const queryFnType = query.queryKey[1];
          if (queryFnType !== "searchCommentsInfinite") return false;
          try {
            const filter = query.queryKey[2] as ApiBlogCommentsSearchFilter;
            return filter.post_id === postId;
          } catch (err) {
            console.log(err);
            return false;
          }
        },
      },
      (data) => {
        if (!data) return data;

        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            items: page.items.map((comment) =>
              comment.id === commentId
                ? {
                    ...comment,
                    is_read: true,
                  }
                : comment
            ),
          })),
        };
      }
    );
  };

  const sendCommentIds = useCallback(async (postId: number) => {
    if (commentIdsBuffer.length === 0) return;

    const commentIds = [...commentIdsBuffer];

    commentIdsBuffer = [];

    const result = await apiBlog.readComments(postId, commentIds);

    if (result.error) throw new ApiErrorException(result.error);

    commentIds.forEach((commentId) => {
      updateReadFlagInCache(postId, commentId);
    });
    return { postId, commentIds };
  }, []);

  const addCommentId = useCallback(
    (postId: number, commentId: number) => {
      if (!commentIdsBuffer.includes(commentId)) {
        commentIdsBuffer.push(commentId);
      }

      if (!timerForReadComments) {
        timerForReadComments = setTimeout(() => {
          sendCommentIds(postId).then(() => {
            timerForReadComments = null;

            if (commentIdsBuffer.length > 0) {
              timerForReadComments = setTimeout(() => sendCommentIds(postId), 2000);
            }
          });
        }, 2000);
      }
    },
    [sendCommentIds]
  );

  return useMutation({
    mutationFn: async function ({ postId, commentId }: { postId: number; commentId: number }) {
      addCommentId(postId, commentId);
      return { postId, commentId };
    },
    onMutate: () => {
      return () => {
        if (timerForReadComments) {
          clearTimeout(timerForReadComments);
          timerForReadComments = null;
        }
      };
    },
  });
};

export const useBlogReadCommentNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      notificationId,
      commentId,
    }: {
      notificationId: number;
      commentId: number;
    }) {
      const result = await apiNotifications.markAsRead(notificationId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return { commentId, notificationId };
    },
    onMutate: () => {
      queryClient.invalidateQueries({
        queryKey: employeeTodayKeys.getToday._def,
      });
    },
  });
};

export const useBlogDeleteComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({ postId, commentId }: { postId: number; commentId: number }) {
      const result = await apiBlog.deleteComment(postId, commentId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return { postId };
    },
    onSuccess: ({ postId }) => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          if (query.queryKey[0] !== "blog") return false;

          const queryFnType = query.queryKey[1];
          if (queryFnType !== "searchCommentsInfinite") return false;

          try {
            const filter = query.queryKey[2] as ApiBlogCommentsSearchFilter;
            return filter.post_id === postId;
          } catch (err) {
            console.log(err);
            return false;
          }
        },
      });
    },
  });
};

export const useBlogEditComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      postId,
      commentId,
      text,
      fileUuids,
    }: {
      postId: number;
      commentId: number;
      text: string;
      fileUuids?: string[];
    }) {
      const result = await apiBlog.updateComment(postId, commentId, {
        text,
        file_uuids: fileUuids,
      });
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return { postId, comment: result.data };
    },
    onSuccess: ({ postId }) => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          if (query.queryKey[0] !== "blog") return false;

          const queryFnType = query.queryKey[1];
          if (queryFnType !== "searchCommentsInfinite") return false;

          try {
            const filter = query.queryKey[2] as ApiBlogCommentsSearchFilter;
            return filter.post_id === postId;
          } catch (err) {
            console.log(err);
            return false;
          }
        },
      });
    },
  });
};

export const useBlogChannelDelete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (id: number) {
      const result = await apiBlog.deleteChannel(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: blogKeys.myChannels._def,
      });
    },
  });
};

export interface SaveChannelParams {
  id?: number;
  payload: ApiBlogChannelSave;
}

export const useBlogChannelSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({ id, payload }: SaveChannelParams) {
      const result = !id
        ? await apiBlog.createChannel(payload)
        : await apiBlog.updateChannel(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: blogKeys.myChannels._def,
      });

      queryClient.invalidateQueries({
        queryKey: blogKeys.channelDetails._def,
        exact: false,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useBlogMyChannelsUnreadCount = (enabled: boolean = true) => {
  return useQuery({
    ...blogKeys.myChannelsUnreadCount(),
    enabled,
  });
};

export const useBlogPostViewers = (filter: ApiBlogPostViewersFilter, enabled: boolean = true) => {
  return useInfiniteQuery({
    ...blogKeys.postViewers(filter),
    queryFn: async ({ pageParam = 1, signal }) => {
      const result = await apiBlog.getPostViewers({ ...filter, page_number: pageParam }, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPageParam) =>
      lastPageParam && lastPageParam.metadata.page_number < lastPageParam.metadata.total_pages
        ? lastPageParam.metadata.page_number + 1
        : undefined,
    enabled,
  });
};

export const useBlogPostDetails = (id: number, enabled: boolean = true) => {
  return useQuery({
    ...blogKeys.postDetails(id),
    enabled,
  });
};

export const useBlogPostsSearch = (filter: ApiBlogPostsSearchFilter, enabled: boolean = true) => {
  return useInfiniteQuery({
    queryKey: blogKeys.searchPostsInfinite(filter).queryKey,
    queryFn: async ({ pageParam = 1, signal }) => {
      const result = await apiBlog.searchPosts({ ...filter, page_number: pageParam }, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPageParam) =>
      lastPageParam && lastPageParam.metadata.page_number < lastPageParam.metadata.total_pages
        ? lastPageParam.metadata.page_number + 1
        : undefined,
    enabled,
  });
};

export const useBlogCommentsSearch = (
  filter: ApiBlogCommentsSearchFilter,
  enabled: boolean = true
) => {
  return useInfiniteQuery({
    queryKey: blogKeys.searchCommentsInfinite(filter).queryKey,
    queryFn: async ({ pageParam = 1 }) => {
      const result = await apiBlog.searchComments(filter.post_id, {
        ...filter,
        page_number: pageParam,
      });
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPageParam) =>
      lastPageParam && lastPageParam.metadata.page_number < lastPageParam.metadata.total_pages
        ? lastPageParam.metadata.page_number + 1
        : undefined,
    enabled,
  });
};

interface SavePostParams {
  id?: number;
  payload: ApiBlogPostSave;
}

export const useBlogPostSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async function ({ id, payload }: SavePostParams) {
      const result = !id
        ? await apiBlog.createPost(payload)
        : await apiBlog.updatePost(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useBlogPostDelete = () => {
  return useMutation({
    mutationFn: async function (id: number) {
      const result = await apiBlog.deletePost(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {},
  });
};

export const useBlogSaveReactionToComment = () => {
  const queryClient = useQueryClient();
  const { data: employeeMe } = useEmployeeMe();

  return useMutation({
    mutationFn: async function ({
      commentId,
      postId,
      emoji,
      isNew,
    }: {
      commentId: number;
      postId: number | undefined;
      emoji: string;
      isNew: boolean;
    }) {
      const result = isNew
        ? await apiComment.addReaction(commentId, emoji)
        : await apiComment.deleteReaction(commentId, emoji);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return { result: result.data, commentId, isNew, emoji, postId };
    },
    onSuccess: ({ commentId, isNew, emoji, postId }) => {
      if (!postId) return;

      const reactionsAfterUpdate = (comment: ApiComment) => {
        const existingReactionIndex = comment.reactions?.findIndex((r) => r.emoji === emoji) ?? -1;

        let updatedReactions = [...(comment.reactions || [])];

        if (isNew) {
          if (existingReactionIndex !== -1) {
            updatedReactions[existingReactionIndex] = {
              ...updatedReactions[existingReactionIndex],
              count: updatedReactions[existingReactionIndex].count + 1,
              is_mine: true,
              employee_ids: employeeMe
                ? [...updatedReactions[existingReactionIndex].employee_ids, employeeMe.id]
                : [...updatedReactions[existingReactionIndex].employee_ids],
            };
          } else {
            updatedReactions.push({
              emoji,
              count: 1,
              is_mine: true,
              employee_ids: employeeMe ? [employeeMe.id] : [],
            });
          }
        } else {
          if (existingReactionIndex !== -1) {
            const currentReaction = updatedReactions[existingReactionIndex];

            if (currentReaction.count <= 1) {
              updatedReactions = updatedReactions.filter((r) => r.emoji !== emoji);
            } else {
              updatedReactions[existingReactionIndex] = {
                ...currentReaction,
                count: currentReaction.count - 1,
                is_mine: false,
                employee_ids: currentReaction.employee_ids.filter((id) => id !== employeeMe?.id),
              };
            }
          }
        }

        return updatedReactions;
      };

      queryClient.setQueriesData<InfiniteData<ApiPagedData<ApiComment>>>(
        {
          predicate: (query) => {
            if (query.queryKey[0] !== "blog") return false;
            const queryFnType = query.queryKey[1];
            if (queryFnType !== "searchCommentsInfinite") return false;
            try {
              const filter = query.queryKey[2] as ApiBlogCommentsSearchFilter;
              return filter.post_id === postId;
            } catch (err) {
              console.log(err);
              return false;
            }
          },
        },
        (data) => {
          if (!data) return data;

          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              items: page.items.map((comment) =>
                comment.id === commentId
                  ? {
                      ...comment,
                      reactions: reactionsAfterUpdate(comment),
                    }
                  : comment
              ),
            })),
          };
        }
      );
    },
  });
};
